// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — command.run execution adapter (ABB-S4, SM-1..4)
// ═══════════════════════════════════════════════════════════════════════════
//
// The `command.run` rpc handler: the ONLY browser write path for a forwarded
// agent turn. It is a TRANSPORT ADAPTER over the EXISTING AI safe contour
// (`runAiCommandBatchThroughSafeContour` — parse → guard → grouped-undo →
// dispatch → audit, UNCHANGED, INV-WRITE/INV-RESULT) — it adds NO parallel write
// path and NO contour modification.
//
// THE PIPELINE (spec lines 123-130 / SM-1):
//   1. epoch-DROP    — the client already dropped a stale-epoch rpc BEFORE this
//                      handler is called (`agentBridgeClient.onRpc`); the handler
//                      re-asserts the welcome epoch is still current (a soft re-key
//                      could have advanced it between dispatch + this body) and the
//                      pre-commit self-check re-reads it again at commit. NEVER
//                      reads `expectedSession` (STRIPPED from the payload — P1).
//   2. PRECONDITION  — BROWSER-side adjudication (companion reads none, INV-1):
//                      `manifestVersion` vs live; `baseSnapshotRevision` EXACT vs
//                      live (unless `force`); `baseGeometry/Table/ContextRevision`
//                      vs live (unless `force`). `force` is revision-ONLY — it NEVER
//                      bypasses the epoch-drop / session quad (the companion's,
//                      force-immune).
//   3. CONTOUR       — run the parsed batch through `runAiCommandBatchThroughSafeContour`
//                      with a CONTOUR-BOUND dispatch SINK (`cmd => getState().dispatch(cmd)`,
//                      wrapped — see step 4). The handler NEVER holds a raw
//                      `getState().dispatch` / `window.editorDispatch` handle (P1,
//                      SM-3). There is NO "already-an-EditorCommand" fast path: a raw
//                      `EditorCommand`/`geometry/*` literal in the batch fails the
//                      contour's `parseAiCommandBatch` → `AiCommandValidationError`
//                      422, never dispatched.
//   4. PRE-COMMIT    — the contour is synchronous + exposes no pre-commit seam, so the
//      SELF-CHECK      injected sink INTERCEPTS the `history/commit-batch` command (the
//                      LAST the contour dispatches), re-reads epoch/role + the
//                      `performance.now` deadline, and THROWS `SupersededError` /
//                      `DeadlineError` BEFORE the commit reaches the store → the
//                      contour's OWN catch fires `history/abort-batch` + rethrows → a
//                      TRUE pre-commit rollback with NO contour change (INV-RESULT).
//   5. AUGMENT       — after the synchronous return: browser-AUTHORITATIVE `batchHash`
//                      (RFC-8785 JCS SHA-256 over the canonical PARSED batch — NOT the
//                      raw forwarded batch, so Zod-defaulted fields hash identically) +
//                      the `[preHighWater, postHighWater)` audit range (ABB-S5 owns the
//                      staging; S4 reads the high-water) + the ACTUALLY-dispatched
//                      `dispatchedCount`/`commands` → reply
//                      `rpc-result{batchHash, result:{grouped,dispatchedCount,commands,auditRefs,createdEntityIds,affectedEntityIds,removedEntityIds}}`
//                      (the T2/T3 created/affected/removed id lists are the agent's create→read→erase handles).
//                      The rpc-ERROR terminal also echoes `batchHash` (SM-2; omitted
//                      only on a pre-parse reject — companion provisional fallback).
//   6. REJECT MAP    — explicit `instanceof` per class → taxonomy code (see
//                      `rejectMapping.ts`); a contour class NEVER reaches the 500
//                      fallback (P2).
//   7. SINGLE-IN-FLIGHT — claim the shared latch (`inFlightLatch.ts`) so a bridge
//                      turn never NESTS into a panel / window-seam batch (P2). A held
//                      latch → `command-in-flight` 409 (bridge-vs-LOCAL).
//   8. DISTINCT PRINCIPAL — run the contour inside `beginBridgeTurn()/endBridgeTurn()`
//                      so a lock/authority check during the turn sees the non-user
//                      bridge principal (P3; ABB-S5 also reads it for audit attribution).
//
// DEP-GRAPH (ПРИОРИТЕТ #2): imports ONLY sibling agentBridge leaves +
// `@dxf-ai-editor/ai-commands` types (an ALLOWED direction). EVERY editor coupling
// (dispatch / getState / revision getters / manifest version / ai-context / the
// contour function / role-epoch getters / the principal marker) arrives via the
// `CommandRunHandlerDeps` DI object — the module imports NO editorStore, NO
// `ai/aiSafeContour` (the contour function is injected, typed structurally), so
// `npm run deps:graph` stays cycles=0. S7's editorStore mount supplies the deps.
// ═══════════════════════════════════════════════════════════════════════════

import {
  AI_COMMAND_SOURCE,
  parseAiCommandBatch,
  type AiCommand,
} from "@dxf-ai-editor/ai-commands";

import {
  projectStagedAuditEvents,
  type StagedAuditEntry,
} from "./auditStaging";
import { computeBatchHash, type BatchHashPreconditions } from "./batchHash";
import type { RpcHandler, RpcHandlerOutcome } from "./clientTypes";
import {
  type InFlightHolder,
  releaseInFlight,
  tryAcquireInFlight,
} from "./inFlightLatch";
import {
  CommandInFlightError,
  DeadlineError,
  MalformedBatchError,
  MalformedPreconditionError,
  mapRejectToRpcError,
  PreconditionStaleError,
  SupersededError,
} from "./rejectMapping";

/** The latch holder this handler claims (bridge-vs-LOCAL coordination). */
const BRIDGE_HOLDER: InFlightHolder = "bridge";

/**
 * The history-batch source the contour stamps on its begin/commit/abort
 * (`AI_COMMAND_SOURCE = "system"`, blockDispatcherShared). The pre-commit
 * self-check's rollback `history/abort-batch` carries the SAME source so it
 * targets the contour's open batch (one source of truth — no hardcoded literal).
 */
const CONTOUR_SOURCE = AI_COMMAND_SOURCE;

// ── the structural contour seam (injected; NEVER imported, to stay leaf) ─────

/**
 * A structural mirror of `aiSafeContour.AiSafeContourResult` — the contour result
 * the handler augments. Declared HERE (not imported from `ai/aiSafeContour`) so the
 * agentBridge graph adds no edge to that module (ПРИОРИТЕТ #2). `commands` is the
 * translated `EditorCommand[]` (typed `unknown[]` here — the handler only COUNTS +
 * echoes them, never interprets them, INV-1).
 */
export interface ContourResult {
  readonly commands: readonly unknown[];
  readonly grouped: boolean;
  /**
   * The CANONICAL PARSED batch (`parseAiCommandBatch` output, Zod-defaulted +
   * refined). The browser-authoritative `batchHash` is computed over THIS (not the
   * raw forwarded batch) so two logically-identical turns differing only in an
   * omitted-vs-explicit defaulted field hash identically (INV-HASH; mirrors
   * `aiSafeContour.AiSafeContourResult.parsed`).
   */
  readonly parsed: readonly AiCommand[];
  /**
   * T2 (agent-editing) — the ids of the entities this turn CREATED (mirrors
   * `aiSafeContour.AiSafeContourResult.createdEntityIds`, ≡ `EntityMutation.added`).
   * The handler echoes these VERBATIM into the rpc-result `result.createdEntityIds`
   * so the forwarded agent can address what it just made (the create→read→erase
   * loop). These are the SAME stable `DrawingEntity.id`s the read-API (T1) projects
   * and erase (T3) targets. Optional (a structural-spy contour may omit it →
   * surfaced as `[]`).
   */
  readonly createdEntityIds?: readonly string[];
  /**
   * T2 (agent-editing) — the ids of the EXISTING entities this turn TOUCHED (mirrors
   * `aiSafeContour.AiSafeContourResult.affectedEntityIds`, ≡ `EntityMutation.updated`).
   * MOVE / ROTATE / SCALE / etc. land here (modified in place, not created). Echoed
   * into the rpc-result `result.affectedEntityIds`. Optional → surfaced as `[]`.
   */
  readonly affectedEntityIds?: readonly string[];
  /**
   * T3 (agent-editing) — the ids of the entities this turn REMOVED (mirrors
   * `aiSafeContour.AiSafeContourResult.removedEntityIds`, ≡ `EntityMutation.removed`).
   * DELETE_ENTITIES (ERASE) of a SPACE entity lands here. Echoed into the rpc-result
   * `result.removedEntityIds` so the forwarded agent can tell EXACTLY which ids its
   * ERASE deleted — a requested delete of a NON-space id (e.g. a block-def `attdef-*`,
   * which `shell/delete-entities` cannot remove) is ABSENT here, making that no-op
   * NON-SILENT (the create→delete loop is verifiable). Optional → surfaced as `[]`.
   */
  readonly removedEntityIds?: readonly string[];
}

/** A dispatch sink — exactly the shape the contour dispatches each command through. */
export type ContourDispatch = (command: unknown) => void;

/**
 * The injected safe-contour function — the SAME `runAiCommandBatchThroughSafeContour`
 * the `AiDraftingPanel` + window seam use, passed via DI so this module never imports
 * `ai/aiSafeContour` (keeping the agentBridge graph a clean leaf). The handler calls
 * it with the parsed batch, the contour-BOUND dispatch SINK (the pre-commit-wrapped
 * `cmd => getState().dispatch(cmd)`), and the runtime context — NEVER a raw dispatch.
 */
export type RunContour = (
  proposed: readonly AiCommand[],
  dispatch: ContourDispatch,
  options: {
    context?: unknown;
    resolveInsertIds?: () => readonly string[];
    /**
     * T2 — the per-turn entity snapshot reader the contour diffs to derive
     * `createdEntityIds` / `affectedEntityIds` (each row is `{ id, fingerprint }`,
     * typed structurally as `unknown` here so this leaf adds no edge to
     * `ai/aiSafeContour`). The mount supplies `collectAiEntitySnapshot`.
     */
    snapshotEntities?: () => readonly unknown[];
    label?: string;
  },
) => ContourResult;

// ── the live-store DI surface the handler reads (typed structurally) ─────────

/** The browser-minted revision counters the precondition gate adjudicates (ABB-S1). */
export interface LiveRevisions {
  readonly snapshotRevision: number;
  readonly geometryRevision: number;
  readonly tableRevision: number;
  readonly contextRevision: number;
}

/**
 * The DI surface for the command.run handler. EVERY editor coupling is here — the
 * module imports no editorStore, no `ai/aiSafeContour`. S7's editorStore mount
 * supplies these from the live store; tests inject mocks.
 *
 * NOTE (spec resolution): the spec said "extend `AgentBridgeClientDeps` with the
 * new DI seams". `AgentBridgeClientDeps` is the TRANSPORT client's deps (socket /
 * handlers / status). The command.run handler's editor-coupling deps are a DISTINCT
 * concern, so they live in THIS dedicated interface; S7 builds the handler from
 * these deps and passes the resulting `RpcHandler` into
 * `AgentBridgeClientDeps.handlers["command.run"]`. This keeps the transport client
 * editor-free and the handler's contract explicit for S5/S7.
 */
export interface CommandRunHandlerDeps {
  /** The injected safe contour (DI; the real `runAiCommandBatchThroughSafeContour`). */
  runContour: RunContour;
  /**
   * The CONTOUR-BOUND raw dispatch sink `cmd => getState().dispatch(cmd)` — the
   * handler WRAPS this (the pre-commit self-check) and passes the WRAPPER into the
   * contour. The handler holds ONLY this closure, NEVER a `getState().dispatch` /
   * `window.editorDispatch` handle (P1, SM-3). S7 supplies `cmd => useEditorStore
   * .getState().dispatch(cmd)`.
   */
  dispatchToContour: ContourDispatch;
  /** Read the live revision counters for the precondition gate (ABB-S1). */
  getRevisions: () => LiveRevisions;
  /** Read the live manifest schemaVersion for the manifest-stale gate. */
  getManifestVersion: () => string;
  /** Read the live audit high-water (`state.audit.nextSequence`) for the auditRefs range. */
  getAuditHighWater: () => number;
  /**
   * Read the live audit-event RING (newest-first, the shape
   * `reducerHelpers.appendAuditEvent` prepends). The `auditRefs` are DERIVED from
   * the events this turn's `[pre, post)` range that SURVIVE in the ring (P2-1) — so
   * the rpc-result `auditRefs` describe the SAME cap-bounded set the ABB-S5 audit
   * PUSH carries (`projectStagedAuditEvents`), never a wider numeric range whose
   * cap-evicted ids have no pushed event body. S7 supplies
   * `() => useEditorStore.getState().state.audit.events`.
   */
  getAuditRing: () => readonly StagedAuditEntry[];
  /** Build the runtime AiDispatchContext the contour translator needs (live shell state). */
  getAiContext: () => unknown;
  /** Read the INSERT ids the contour resolves LAST_INSERT_ID_SENTINEL against. */
  resolveInsertIds?: () => readonly string[];
  /**
   * T2 — read the per-turn entity snapshot (`{ id, fingerprint }` rows) the contour
   * diffs to derive the turn's `createdEntityIds` / `affectedEntityIds`. Passed
   * straight to the contour; the handler echoes the resulting ids into the rpc
   * result. S7 supplies `collectAiEntitySnapshot(getState().geometryDocument)`.
   * Optional — absent → the contour surfaces empty id arrays (inert).
   */
  snapshotEntities?: () => readonly unknown[];
  /**
   * Whether THIS tab is still the current ACTIVE epoch (SessionTracker authority).
   * Re-read at the pre-commit self-check; a demotion mid-turn → SupersededError →
   * pre-commit abort.
   */
  isCurrentActiveEpoch: () => boolean;
  /**
   * The current welcome epoch (SessionTracker authority). Re-read at handler entry
   * + the pre-commit self-check: a soft re-key that advanced the epoch past the
   * dispatch epoch → SupersededError → pre-commit abort.
   */
  getCurrentEpoch: () => number | null;
  /**
   * A monotonic clock in the BROWSER's OWN `performance.now` domain (DI for
   * deterministic tests; default `() => performance.now()`). The deadline is armed
   * RELATIVE to this — `deadlineBudgetMs` is a relative scalar in the companion's
   * clock domain, NEVER an absolute cross-WS timestamp.
   */
  now?: () => number;
  /**
   * Begin/end the ambient "current bridge turn" principal marker around the
   * synchronous contour call (P3 — the distinct execution principal; ABB-S5 also
   * reads it for channel/actor audit attribution). A no-user principal so a
   * COLLAB-locked entity still rejects (the bridge is not the lock owner).
   */
  beginBridgeTurn?: () => void;
  endBridgeTurn?: () => void;
  /**
   * BROWSER-SIDE side channel for the ABB-S5 audit PUSH (round-3 P2-1; round-4 epoch
   * unification). The handler hands the FROZEN pre-await audit snapshot (the SAME
   * projected set the rpc-result `auditRefs` are derived from) to this callback —
   * NEVER through the wire `result` (the companion treats `frame.result` as OPAQUE and
   * fans it out verbatim through POST responses / outcomes / SSE —
   * `wsServer.ts`/`brokerReadFace.ts` — so an enumerable `result.auditEvents` would
   * LEAK the audit bodies AND duplicate the separate audit push; the documented
   * command-result shape is `{grouped,dispatchedCount,commands,auditRefs}` PLUS the T2/T3
   * `createdEntityIds`/`affectedEntityIds`/`removedEntityIds` id lists — never the audit
   * bodies — design doc:127). S7 wires this to
   * `pushObservers.emitTurnAuditOnCommit(snapshot.auditEvents)` so the push body
   * carries the event bodies off-wire.
   *
   * ── round-4 EPOCH-STAMPING (the @codex #376 contradiction, resolved by UNIFICATION) ──
   * SYNCHRONOUS-EMIT CONTRACT (LOAD-BEARING — see the call site): this callback MUST be
   * invoked SYNCHRONOUSLY at commit, BEFORE the `await computeBatchHash` below — there is
   * NO `await` between the contour's synchronous return and this call. Because JS is
   * single-threaded, NO soft re-key can interleave in that window, so at the emit instant
   * `getCurrentEpoch() === ctx.sessionEpoch` (the dispatch epoch — they were re-asserted
   * equal at handler entry and nothing has yielded since). The audit push is therefore
   * stamped with the CURRENT welcome epoch read at this synchronous instant (S5's
   * `sendPush` → `getCurrentEpoch()`), which is BOTH:
   *   • correctly SEGMENTED — it carries the turn's OWN (dispatch) epoch (round-2's goal),
   *     the same epoch the rpc-result terminal settles on via the minted-scope history; AND
   *   • companion-ACCEPTED — `brokerReadFace.onBrowserPush` DROPS any push whose
   *     `frame.sessionEpoch !== currentQuad().sessionEpoch` (524-526). A current-epoch
   *     stamp passes that gate; the OLD round-3 captured-dispatch-epoch OVERRIDE was
   *     exactly what that gate rejected after a soft re-key (the events were silently
   *     discarded — never journaled / SSE'd). WS-FIFO ordering guarantees this push
   *     reaches the companion BEFORE any same-tab `document-changed` re-key frame, so the
   *     companion is still on this epoch when it processes the push; a tab demoted by a
   *     takeover never emits at all (S5's demotion gate).
   * A DEFERRED emission (after the await) would re-break BOTH halves — it could read an
   * advanced `getCurrentEpoch()` (mis-segmented) AND the companion would have re-keyed
   * (dropped). The captured-old-epoch override only existed to paper over a deferred
   * emission; emitting synchronously removes the need for it entirely. Hence NO
   * `dispatchEpoch` is carried here — the push reads the current epoch at the synchronous
   * emit instant.
   *
   * Called ONLY on a COMMITTED turn (never on a thrown / aborted turn — the catch arm
   * never reaches it). Absent until S7 wires it (the snapshot is then simply not
   * surfaced — inert, INV-OFF).
   */
  onTurnAuditCommitted?: (snapshot: {
    readonly auditEvents: readonly StagedAuditEntry[];
  }) => void;
}

// ── the forwarded payload shape (the companion forwards it OPAQUELY, INV-1) ───

/**
 * The forwarded command.run payload (SM-1). `expectedSession` is STRIPPED by the
 * companion (INV-OWNERSHIP-SPLIT) — it is NEVER present here and NEVER read (P1).
 * Every field is read DEFENSIVELY (the wire is untrusted).
 */
interface CommandRunPayload {
  batch: unknown;
  baseSnapshotRevision?: unknown;
  baseGeometryRevision?: unknown;
  baseTableRevision?: unknown;
  baseContextRevision?: unknown;
  force?: unknown;
  manifestVersion?: unknown;
  label?: unknown;
}

function asPayload(payload: unknown): CommandRunPayload | null {
  if (typeof payload !== "object" || payload === null) {
    return null;
  }
  return payload as CommandRunPayload;
}

/**
 * Adjudicate ONE revision precondition by EXACT equality (INV-9). A precondition
 * is checked ONLY when the agent SUPPLIED a base value; an ABSENT base
 * (`undefined`) means "no optimistic guard for this axis" — not a mismatch.
 *
 * A PRESENT-but-MALFORMED base (anything that is not a FINITE number — a string
 * `"5"`, `null`, `NaN`, `±Infinity`, an object, …) is REJECTED, NOT silently
 * treated as absent: the companion forwards `base*Revision` OPAQUELY (INV-1), so a
 * wrongly-typed value reaches the handler; dropping the guard would run the command
 * against the live revision (a data-integrity hole). `force` skips the REVISION
 * checks (force is revision-ONLY) so this is not reached when forced.
 *
 * THROWS `MalformedPreconditionError(field)` when present-but-malformed, or
 * `PreconditionStaleError(code)` on a present-valid-but-stale mismatch.
 */
function assertRevision(
  field: string,
  code: PreconditionStaleError["code"],
  base: unknown,
  live: number,
): void {
  if (base === undefined) {
    return; // ABSENT → no optimistic guard on this axis (legitimately skipped).
  }
  if (typeof base !== "number" || !Number.isFinite(base)) {
    // PRESENT but not a finite number → a malformed envelope value (e.g. "5").
    throw new MalformedPreconditionError(field);
  }
  if (base !== live) {
    throw new PreconditionStaleError(code, base, live);
  }
}

/**
 * Build the command.run rpc handler from its DI deps. The returned `RpcHandler` is
 * wired into `AgentBridgeClientDeps.handlers["command.run"]` by S7. It is ASYNC
 * (the batchHash uses Web Crypto) — the transport client awaits it.
 */
export function createCommandRunHandler(deps: CommandRunHandlerDeps): RpcHandler {
  const now = deps.now ?? (() => performance.now());

  return async (ctx): Promise<RpcHandlerOutcome> => {
    // ── 1. epoch re-assert (the client already epoch-dropped a stale rpc; this is
    //    a defensive re-check that the welcome epoch is STILL current at body
    //    entry — a soft re-key could advance it between dispatch + this body). The
    //    pre-commit self-check re-reads epoch/role AGAIN at commit. NEVER reads
    //    expectedSession (stripped, P1). ─────────────────────────────────────────
    const currentEpoch = deps.getCurrentEpoch();
    if (currentEpoch === null || ctx.sessionEpoch !== currentEpoch) {
      return rejectOutcome(new SupersededError());
    }

    // ── 7. SINGLE-IN-FLIGHT: claim the shared latch BEFORE any dispatch so a bridge
    //    turn never nests into an open panel / window-seam batch (P2). A held latch
    //    → command-in-flight 409 (bridge-vs-LOCAL). ──────────────────────────────
    if (!tryAcquireInFlight(BRIDGE_HOLDER)) {
      return rejectOutcome(new CommandInFlightError());
    }

    try {
      const payload = asPayload(ctx.payload);
      if (payload === null || !Array.isArray(payload.batch)) {
        // A malformed payload (no batch array) is an agent grammar error → 422.
        // The contour would also reject it, but a non-array `batch` never reaches
        // `parseAiCommandBatch` safely, so reject up front.
        throw new MalformedBatchError();
      }

      // ── 2. PRECONDITION adjudication (BROWSER-side, INV-1). `force` is
      //    revision-ONLY: it skips the four revision checks + manifest is checked
      //    REGARDLESS (the manifest gate is not a revision precondition — a stale
      //    build cannot be force-applied). ──────────────────────────────────────
      const live = deps.getRevisions();
      const forced = payload.force === true;

      // manifest-stale: checked even under force (a stale build is not force-able).
      // ABSENT → no manifest guard; PRESENT-but-non-string → a malformed envelope
      // value (reject, never silently skip — symmetric with the revision axes).
      if (payload.manifestVersion !== undefined) {
        if (typeof payload.manifestVersion !== "string") {
          throw new MalformedPreconditionError("manifestVersion");
        }
        const liveManifest = deps.getManifestVersion();
        if (payload.manifestVersion !== liveManifest) {
          throw new PreconditionStaleError(
            "manifest-stale",
            payload.manifestVersion,
            liveManifest,
          );
        }
      }

      if (!forced) {
        assertRevision("baseSnapshotRevision", "snapshot-stale", payload.baseSnapshotRevision, live.snapshotRevision);
        assertRevision("baseGeometryRevision", "geometry-stale", payload.baseGeometryRevision, live.geometryRevision);
        assertRevision("baseTableRevision", "table-stale", payload.baseTableRevision, live.tableRevision);
        assertRevision("baseContextRevision", "context-stale", payload.baseContextRevision, live.contextRevision);
      }

      // ── 5a. read the audit high-water BEFORE the synchronous contour call (the
      //    [pre, post) range is this turn's audit events — `getAuditHighWater()` is
      //    the EXCLUSIVE `nextSequence`; ABB-S5 owns the staging, S4 reads the bound
      //    + reports the SURVIVING projected ids as `auditRefs` — derived from the
      //    SAME projection the push carries so the two agree at the ring cap, P2-1). ─
      const preHighWater = deps.getAuditHighWater();

      // ── 4. arm the pre-commit self-check: a deadline in the browser's OWN
      //    performance.now domain (deadlineBudgetMs is a RELATIVE scalar — add it to
      //    NOW, never treat it as an absolute cross-WS timestamp). The wrapped sink
      //    intercepts `history/commit-batch` (the LAST command the contour
      //    dispatches), re-reads epoch/role + the deadline, and on a demotion /
      //    over-budget it performs a TRUE pre-commit rollback then throws.
      //
      //    SPEC RESOLUTION (a corrected contour-structure assumption): the design
      //    said "throw before commit → the contour's OWN catch fires abort-batch".
      //    But the contour dispatches `history/commit-batch` OUTSIDE its try/catch
      //    (`aiSafeContour.ts` — the try wraps ONLY the command sequence; the commit
      //    is post-try). So throwing AT commit would
      //    NOT be caught by the contour → the batch would stay OPEN (fail-open, a
      //    ПРИОРИТЕТ #1 downgrade). Instead, the sink ITSELF dispatches
      //    `history/abort-batch` through the raw sink BEFORE throwing — rolling the
      //    open batch back (the store's abort reverts the inverses + clears the open
      //    batch, `editorStore.ts` `history/abort-batch` handler) — then throws. This is a TRUE
      //    pre-commit rollback with NO contour modification (INV-RESULT): the
      //    commit-batch never reaches the store, and the abort closes the batch so
      //    nothing leaks into the next turn. (The additive contour `beforeCommit`
      //    option is the documented alternative; the sink-self-abort needs no
      //    contour change and is taken here.) `AI_COMMAND_SOURCE` is `"system"` (the
      //    source the contour stamps), so the abort matches the open batch's source.
      const deadlineAt =
        typeof ctx.deadlineBudgetMs === "number" && Number.isFinite(ctx.deadlineBudgetMs)
          ? now() + ctx.deadlineBudgetMs
          : null;

      // ── 5c. ACTUALLY-DISPATCHED tally (P2). The contour may DELIBERATELY DROP a
      //    command (e.g. the trailing INSERT_BLOCK attribute sentinel when no insert
      //    id resolves — `aiSafeContour.ts dispatchAiCommandSequence`), so
      //    `result.commands` (the TRANSLATED list) OVERSTATES what reached the store.
      //    We count + collect from the WRAPPED sink — the SAME path every store-bound
      //    command flows through — EXCLUDING the begin/commit/abort batch-control
      //    commands (those are grouping bookkeeping, not the agent's dispatched
      //    commands). This is the authoritative `dispatchedCount` + echoed `commands`.
      const dispatchedCommands: unknown[] = [];

      const wrappedDispatch: ContourDispatch = (command) => {
        if (isCommitBatch(command)) {
          // PRE-COMMIT SELF-CHECK — re-read the live authority right before commit.
          const superseded =
            !deps.isCurrentActiveEpoch() || deps.getCurrentEpoch() !== ctx.sessionEpoch;
          const overDeadline = deadlineAt !== null && now() > deadlineAt;
          if (superseded || overDeadline) {
            // TRUE pre-commit rollback: abort the OPEN batch (the store reverts +
            // closes it) BEFORE the commit reaches the store, then throw. The
            // abort carries the contour's source so it targets the open batch.
            deps.dispatchToContour({ type: "history/abort-batch", source: CONTOUR_SOURCE });
            throw superseded ? new SupersededError() : new DeadlineError();
          }
        }
        // Tally the real, store-bound agent commands ONLY (skip begin/commit/abort
        // batch-control) — this is what ACTUALLY reached the store this turn.
        if (!isBatchControl(command)) {
          dispatchedCommands.push(command);
        }
        // Forward EVERY command (begin-batch / the commands / and the commit-batch
        // when the self-check passed) to the contour-bound raw sink.
        deps.dispatchToContour(command);
      };

      // ── 8. DISTINCT PRINCIPAL: run the synchronous contour inside the bridge-turn
      //    marker so a lock/authority check sees the non-user bridge principal. The
      //    marker is ALWAYS ended (finally) even on a thrown / aborted turn. ───────
      deps.beginBridgeTurn?.();
      let result: ContourResult;
      try {
        // ── 3. CONTOUR: parse → guard → grouped-undo → dispatch (the raw batch is
        //    ALWAYS parsed; a raw EditorCommand literal fails parse → 422). The
        //    contour is UNCHANGED; the pre-commit self-check lives in the wrapped
        //    sink, not the contour. ───────────────────────────────────────────────
        result = deps.runContour(payload.batch as readonly AiCommand[], wrappedDispatch, {
          context: deps.getAiContext(),
          ...(deps.resolveInsertIds !== undefined
            ? { resolveInsertIds: deps.resolveInsertIds }
            : {}),
          ...(deps.snapshotEntities !== undefined
            ? { snapshotEntities: deps.snapshotEntities }
            : {}),
          ...(typeof payload.label === "string" ? { label: payload.label } : {}),
        });
      } finally {
        deps.endBridgeTurn?.();
      }

      // ── 5b. AUGMENT: the synchronous contour returned successfully (committed or a
      //    zero-command no-op). Compute the browser-authoritative batchHash over the
      //    canonical OPERATION IDENTITY — the CANONICAL PARSED batch (NOT the raw
      //    forwarded batch — the parsed form materializes Zod defaults so two
      //    logically-identical turns hash the same, INV-HASH; P2 #1) PLUS the
      //    optimistic-concurrency preconditions (round-2 P1: the durable outcomeId is
      //    keyed by `{…, batchHash}` and the ledger de-dupes a seen outcomeId, so a
      //    correlationId REUSED post-TTL with the SAME parsed batch but a CORRECTED
      //    precondition must hash DIFFERENTLY — else the corrected outcome is lost). The
      //    hash covers the SAME identity set as the companion's provisional fallback
      //    (`correlation.ts computeEnvelopeHash`) so the two sources agree on identity.
      //    Then read the post high-water + report the ACTUALLY-dispatched count +
      //    commands (P2 #5). ───────────────────────────────────────────────────────
      const postHighWater = deps.getAuditHighWater();

      // ── P2-2: SNAPSHOT the staged audit projection SYNCHRONOUSLY at commit time —
      //    BEFORE any `await` — and use this ONE snapshot for BOTH the rpc-result
      //    `auditRefs` AND the ABB-S5 audit PUSH `events`. The ring is hard-capped at
      //    24 (`reducerHelpers.appendAuditEvent`); browser audit activity DURING the
      //    `await computeBatchHash` (the Web-Crypto digest yields the microtask queue)
      //    could EVICT this turn's just-committed entries from the ring. If `auditRefs`
      //    (or the push) read the LIVE ring AFTER the await, they would become
      //    empty/partial even though `[preHighWater, postHighWater)` still describes
      //    the committed turn — a silent fidelity divergence (ПРИОРИТЕТ #1). Projecting
      //    HERE (synchronously, before the digest) freezes the committed-turn events,
      //    so the snapshot is immune to post-await eviction AND `auditRefs` ≡ push
      //    `events` BY CONSTRUCTION (the P2-1 invariant): both are derived from the
      //    SAME frozen snapshot, never two independent post-await ring reads.
      //
      //    OFF-WIRE side channel (round-3 P2-1): the FULL `auditEvents` BODIES are
      //    handed to `deps.onTurnAuditCommitted` (a browser-side callback S7 wires to
      //    `pushObservers.emitTurnAuditOnCommit`) — NOT placed into the wire `result`.
      //    The companion treats `frame.result` as OPAQUE (`wsServer.ts`:1123-1125 /
      //    `brokerReadFace.ts`:235-250) and fans it out verbatim through POST responses
      //    / outcomes / SSE, so a `result.auditEvents` would LEAK the bodies AND
      //    duplicate the separate audit push. The wire `result` carries the documented
      //    `{grouped,dispatchedCount,commands,auditRefs}` PLUS the T2/T3 created/affected/removed
      //    id lists — NEVER the audit bodies (`auditRefs` is the id list — KEEP it,
      //    derived from the SAME snapshot so refs ≡ push). Captured + fired
      //    SYNCHRONOUSLY (before the hash await) so the snapshot is the frozen pre-await
      //    set even if the ring evicts during the digest — AND so the audit push is
      //    stamped with the CURRENT(=dispatch) epoch at the synchronous instant (round-4;
      //    see `onTurnAuditCommitted`'s SYNCHRONOUS-EMIT CONTRACT).
      //
      //    HIGH-WATER semantics: `getAuditHighWater()` returns `state.audit.nextSequence`
      //    (the seq that WILL be assigned to the NEXT event — an EXCLUSIVE upper bound;
      //    `reducerHelpers.ts pushAuditEvent` assigns `sequence = nextSequence` then
      //    increments). So `preHighWater` is this turn's FIRST event seq (INCLUSIVE) and
      //    `postHighWater` is the next-after-this-turn seq (EXCLUSIVE): the turn's own
      //    events are the HALF-OPEN range `[preHighWater, postHighWater)`. The projection
      //    is cap-bounded — the ring slices to 24, so a >24-command turn evicts its own
      //    oldest rows; a cap-evicted event is in NEITHER `auditRefs` NOR the push (the
      //    contract is "the surviving ring entries", not the full numeric span). Ascending
      //    by sequence; an empty range (a zero-command no-op) → an empty array.
      const auditEvents = projectStagedAuditEvents(
        deps.getAuditRing(),
        preHighWater,
        postHighWater,
      );
      const auditRefs = auditEvents.map((event) => event.sequence);

      // ── round-4: carry the FROZEN audit snapshot off-wire to S7, fired SYNCHRONOUSLY
      //    HERE (BEFORE the `await computeBatchHash` below). This synchronous timing is
      //    LOAD-BEARING for the epoch stamp: at this instant NO soft re-key can have
      //    interleaved (JS is single-threaded; nothing has yielded since the contour
      //    returned), so `deps.getCurrentEpoch() === ctx.sessionEpoch` (the dispatch
      //    epoch — re-asserted equal at handler entry). S5's `emitTurnAuditOnCommit` →
      //    `sendPush` therefore stamps the audit push with the CURRENT(=dispatch) epoch,
      //    which is BOTH correctly segmented (the turn's own epoch the rpc-result settles
      //    on) AND companion-accepted (`brokerReadFace.onBrowserPush` drops any push whose
      //    `sessionEpoch !== currentQuad().sessionEpoch`; WS-FIFO delivers this push before
      //    any same-tab `document-changed` re-key frame). The round-3 captured-old-epoch
      //    OVERRIDE is GONE — it only existed for a deferred emission, which the companion
      //    gate rejects. A deferred call here would re-break both halves (mis-segment +
      //    drop), so the contract is: emit synchronously, read current at the emit instant.
      deps.onTurnAuditCommitted?.({ auditEvents });

      const batchHash = await computeBatchHash(result.parsed, extractPreconditions(payload));

      return {
        ok: true,
        batchHash,
        result: {
          grouped: result.grouped,
          // The ACTUALLY-dispatched, store-bound command tally (the contour may drop
          // a sentinel) — NOT `result.commands.length` (the TRANSLATED list, which
          // would overstate when a command was dropped).
          dispatchedCount: dispatchedCommands.length,
          commands: dispatchedCommands,
          auditRefs,
          // T2/T3 (agent-editing) — the JSON-serializable string-id arrays the agent
          // needs to address what this turn made (the create→read→erase loop). These
          // are the SAME stable `DrawingEntity.id`s the read-API (T1) projects + erase
          // (T3) targets. `createdEntityIds` ≡ `EntityMutation.added`,
          // `affectedEntityIds` ≡ `EntityMutation.updated`,
          // `removedEntityIds` ≡ `EntityMutation.removed` (what the turn's ERASE actually
          // deleted — a NON-space id the space-only `shell/delete-entities` cannot remove
          // is ABSENT, so a failed delete is NON-SILENT). Defaulted to `[]` so the wire
          // result always carries all three keys (a structural-spy contour omits them).
          // The companion forwards `result` OPAQUELY (commandsRoute.ts), so these flow
          // verbatim through the POST response with no companion code change.
          createdEntityIds: [...(result.createdEntityIds ?? [])],
          affectedEntityIds: [...(result.affectedEntityIds ?? [])],
          removedEntityIds: [...(result.removedEntityIds ?? [])],
          // NOTE (P2-1): the full audit-event BODIES are NEVER on the wire `result` —
          // they ride the OFF-WIRE `onTurnAuditCommitted` side channel above. The wire
          // result carries the documented {grouped,dispatchedCount,commands,auditRefs}
          // PLUS the T2/T3 created/affected/removed id lists; `auditRefs` is the documented
          // id list (derived from the SAME snapshot the push carries, so refs ≡ push body
          // ids by construction).
        },
      };
    } catch (error) {
      // ── 6. REJECT MAP: a contour throw (AiCommandValidationError / ...), the
      //    precondition gate, or the pre-commit self-check (Superseded / Deadline)
      //    → its canonical taxonomy code. The contour ALREADY aborted its batch
      //    (begin-batch…abort-batch) on a mid-turn throw, so the document is
      //    unchanged. A contour class NEVER reaches the 500 fallback (P2).
      //
      //    rpc-error batchHash (P2 #3, SM-2): the companion's `CommandTerminal`
      //    expects a `batchHash` on the rpc-ERROR terminal too (correlation.ts) — it
      //    adopts the browser-authoritative hash as the durable-ledger identity. We
      //    hash the CANONICAL PARSED batch when the raw batch parses (a contour
      //    throw AFTER parse — pre-commit Superseded/Deadline, or a translate/guard
      //    reject); when the batch does NOT parse (an AiCommandValidationError, or a
      //    pre-parse reject — epoch-drop / latch / precondition / malformed) there is
      //    no canonical parsed form, so batchHash is OMITTED. The wire contract
      //    TOLERATES an absent batchHash on a pre-parse reject: `buildRpcErrorFrame`
      //    omits the field and the companion FALLS BACK to its own provisional
      //    envelope hash (`correlation.ts provisionalHashFor`) for the ledger
      //    identity — so the identity stays payload-specific without the browser
      //    fabricating a hash over an unparseable batch. ────────────────────────────
      return rejectOutcome(error, await bestEffortBatchHash(ctx.payload));
    } finally {
      // Always release the latch — a leaked acquire would block the next turn.
      releaseInFlight(BRIDGE_HOLDER);
    }
  };
}

/**
 * Map a thrown error to the `{ok:false, ...}` rpc-error outcome the client
 * serializes. `batchHash` (P2 #3) is the browser-authoritative hash echoed on the
 * rpc-ERROR terminal too (SM-2); it is `undefined` when the batch did not parse
 * (no canonical form) — the wire frame then OMITS the field and the companion
 * falls back to its provisional envelope hash.
 */
function rejectOutcome(error: unknown, batchHash?: string): RpcHandlerOutcome {
  const mapped = mapRejectToRpcError(error);
  return {
    ok: false,
    ...(batchHash !== undefined ? { batchHash } : {}),
    code: mapped.code,
    message: mapped.message,
    ...(mapped.detail !== undefined ? { detail: mapped.detail } : {}),
  };
}

/**
 * Best-effort browser-authoritative `batchHash` for a REJECT terminal (P2 #3). Hash
 * the canonical OPERATION IDENTITY — the CANONICAL PARSED batch (the SAME
 * `parseAiCommandBatch` the contour runs) PLUS the same optimistic-concurrency
 * preconditions the success terminal hashes (round-2 P1) — so a post-parse reject
 * (pre-commit Superseded/Deadline, a translate/guard throw) echoes the IDENTICAL hash
 * the success terminal would have for the SAME operation, and a corrected-precondition
 * retry hashes DIFFERENTLY (both terminals are identity-consistent). Returns
 * `undefined` (→ the frame omits batchHash, companion falls back to its provisional
 * hash) when:
 *   • the payload is not an object / has no `batch` array (a pre-parse malformed
 *     reject — there is no batch to canonicalize), OR
 *   • `parseAiCommandBatch` throws (an AiCommandValidationError — the batch has no
 *     valid canonical parsed form), OR
 *   • `computeBatchHash` throws (a non-serializable parsed value).
 * Never throws — a hashing failure must never mask the original reject.
 */
async function bestEffortBatchHash(payload: unknown): Promise<string | undefined> {
  if (typeof payload !== "object" || payload === null) {
    return undefined;
  }
  const batch = (payload as { batch?: unknown }).batch;
  if (!Array.isArray(batch)) {
    return undefined;
  }
  try {
    const parsed = parseAiCommandBatch(batch);
    return await computeBatchHash(parsed, extractPreconditions(payload as CommandRunPayload));
  } catch {
    return undefined; // unparseable / non-serializable → omit (companion fallback).
  }
}

/**
 * Extract the optimistic-concurrency preconditions the batchHash identity covers from
 * the forwarded payload (round-2 P1). ONE truth for BOTH the success terminal AND the
 * rpc-error best-effort terminal so the two emit an identity-consistent hash. Carries
 * each field VERBATIM (the canonical-identity builder normalizes `force` + omits absent
 * keys); the companion's provisional fallback (`computeEnvelopeHash`) covers the SAME
 * set (`commandsRoute.ts buildOpaqueRpcPayload`), so the browser hash + the fallback
 * agree on which operations are distinct.
 */
function extractPreconditions(payload: CommandRunPayload): BatchHashPreconditions {
  return {
    baseSnapshotRevision: payload.baseSnapshotRevision,
    baseGeometryRevision: payload.baseGeometryRevision,
    baseTableRevision: payload.baseTableRevision,
    baseContextRevision: payload.baseContextRevision,
    manifestVersion: payload.manifestVersion,
    force: payload.force,
    label: payload.label,
  };
}

/** Whether a dispatched command is the `history/commit-batch` the contour ends with. */
function isCommitBatch(command: unknown): boolean {
  return (
    typeof command === "object" &&
    command !== null &&
    (command as { type?: unknown }).type === "history/commit-batch"
  );
}

/**
 * Whether a dispatched command is a history BATCH-CONTROL command (begin / commit /
 * abort) — grouping bookkeeping the contour brackets every turn with, NOT one of the
 * agent's dispatched commands. Excluded from the `dispatchedCount` / echoed
 * `commands` tally (P2 #5) so the agent never sees the grouping commands counted as
 * its own.
 */
function isBatchControl(command: unknown): boolean {
  if (typeof command !== "object" || command === null) {
    return false;
  }
  const type = (command as { type?: unknown }).type;
  return (
    type === "history/begin-batch" ||
    type === "history/commit-batch" ||
    type === "history/abort-batch"
  );
}

